import { useStore } from 'zustand';
import { useCurrentStateAndParams } from '@uirouter/react';
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  PropsWithChildren,
  useEffect,
} from 'react';

import { isEdgeAdmin, isPureAdmin } from '@/portainer/users/user.helpers';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { User } from '@/portainer/users/types';
import {
  CurrentUserResponse,
  useLoadCurrentUser,
  useUserEnvironmentAuthorizations,
  useUserKubernetesNamespaceAuthorizations,
} from '@/portainer/users/queries/useLoadCurrentUser';

import { useEnvironment } from '../portainer/environments/queries';
import { isBE } from '../portainer/feature-flags/feature-flags.service';
import { AuthorizationMap } from '../portainer/users/RolesView/types';
import {
  Authorization,
  Authorizations,
} from '../portainer/rbac/authorizations';
import { isKubernetesEnvironment } from '../portainer/environments/utils';

import { environmentStore } from './current-environment-store';
import { currentUserStore } from './current-user-store';

const ALL_NAMESPACES_ACCESS = Authorizations.Kubernetes.Access.All.Namespaces;

interface State {
  user?: CurrentUserResponse;
  authorizations?: { [endpointId: number]: AuthorizationMap };
  namespaceAuthorizations?: {
    [endpointId: number]: Record<string, AuthorizationMap>;
  };
  namespaceAuthorizationsLoading?: boolean;
}

export const UserContext = createContext<State | null>(null);
UserContext.displayName = 'UserContext';

/**
 * @deprecated use `useCurrentUser` instead
 */
export const useUser = useCurrentUser;

export function useCurrentUser() {
  const context = useContext(UserContext);

  if (context === null) {
    throw new Error('should be nested under UserProvider');
  }

  const {
    user,
    authorizations,
    namespaceAuthorizations,
    namespaceAuthorizationsLoading,
  } = context;
  if (typeof user === 'undefined') {
    throw new Error('should be authenticated');
  }

  return useMemo(
    () => ({
      user: {
        ...user,
        EndpointAuthorizations: authorizations,
      },
      isPureAdmin: isPureAdmin(user),
      authorizations,
      namespaceAuthorizations,
      namespaceAuthorizationsLoading,
    }),
    [
      authorizations,
      namespaceAuthorizations,
      namespaceAuthorizationsLoading,
      user,
    ]
  );
}

export function useIsPureAdmin() {
  const { isPureAdmin } = useCurrentUser();
  return isPureAdmin;
}

/**
 * Load the admin status of the user, returning true if the user is edge admin or admin.
 * @param forceEnvironmentId to force the environment id, used where the environment id can't be loaded from the router, like sidebar
 * @returns query result with isLoading and isAdmin - isAdmin is true if the user edge admin or admin.
 */
export function useIsEdgeAdmin({
  forceEnvironmentId,
  noEnvScope,
}: {
  forceEnvironmentId?: EnvironmentId;
  noEnvScope?: boolean;
} = {}) {
  const { user } = useCurrentUser();
  const {
    params: { endpointId },
  } = useCurrentStateAndParams();

  const envId = forceEnvironmentId || endpointId;
  const envScope = typeof noEnvScope === 'boolean' ? !noEnvScope : !!envId;
  const envQuery = useEnvironment(envScope ? envId : undefined);

  if (!envScope) {
    return { isLoading: false, isAdmin: isEdgeAdmin(user) };
  }

  if (envQuery.isLoading) {
    return { isLoading: true, isAdmin: false };
  }

  return {
    isLoading: false,
    isAdmin: isEdgeAdmin(user, envQuery.data),
  };
}

/**
 * Check if the user has some of the authorizations
 *
 * @param authorizations a list of authorizations to check
 * @param forceEnvironmentId to force the environment id, used where the environment id can't be loaded from the router, like sidebar
 * @param adminOnlyCE if true, will return false if the user is not an admin in CE
 * @returns query result with isLoading and authorized - authorized is true if the user has some of the authorizations
 *
 * TODO LATER: refactor to query authorizations for the forceEnvironmentId, or use currentUserStore to fetch the current authorizations
 */
export function useAuthorizations(
  authorizations: Authorization | Authorization[],
  forceEnvironmentId?: EnvironmentId,
  adminOnlyCE = false
) {
  const { user } = useCurrentUser();
  const {
    params: { endpointId },
  } = useCurrentStateAndParams();

  const envQuery = useEnvironment(forceEnvironmentId || endpointId);
  const isAdminQuery = useIsEdgeAdmin({ forceEnvironmentId });

  if (!user) {
    return { authorized: false, isLoading: false };
  }

  if (envQuery.isInitialLoading || isAdminQuery.isLoading) {
    return { authorized: false, isLoading: true };
  }

  if (isAdminQuery.isAdmin) {
    return { authorized: true, isLoading: false };
  }

  if (!isBE && adminOnlyCE) {
    return { authorized: false, isLoading: false };
  }

  return {
    authorized: hasAuthorizations(user, authorizations, envQuery.data?.Id),
    isLoading: false,
  };
}

export function useIsEnvironmentAdmin(adminOnlyCE: boolean = true) {
  return useAuthorizations(['EndpointResourcesAccess'], undefined, adminOnlyCE);
}

/**
 * Returns a per-namespace authorization checker for the given authorizations,
 * rather than a single check against the environment-wide authorization map.
 * Use this to gate actions whose backend enforcement is namespace-scoped
 * (e.g. Kubernetes application actions), where an environment-wide check
 * would incorrectly reflect grants from a namespace-scoped role assigned to
 * a different namespace.
 *
 * Returns a `canAccess` function rather than a single boolean so callers with
 * many items spanning different namespaces (e.g. a datatable) can evaluate
 * authorization per-row without calling a hook per row.
 *
 * @param authorizations a list of authorizations to check
 */
export function useNamespaceAuthorizationChecker(
  authorizations: Authorization | Authorization[]
): { canAccess: (namespace: string) => boolean; isLoading: boolean } {
  const { user, namespaceAuthorizations, namespaceAuthorizationsLoading } =
    useCurrentUser();
  const {
    params: { endpointId },
  } = useCurrentStateAndParams();
  const envQuery = useEnvironment(endpointId);
  const isAdminQuery = useIsEdgeAdmin();
  const envId = envQuery.data?.Id;

  if (envQuery.isInitialLoading || isAdminQuery.isLoading) {
    return { canAccess: () => false, isLoading: true };
  }

  if (isAdminQuery.isAdmin) {
    return { canAccess: () => true, isLoading: false };
  }

  if (envId === undefined) {
    return { canAccess: () => false, isLoading: false };
  }

  if (hasAuthorizations(user, [ALL_NAMESPACES_ACCESS], envId)) {
    const authorized = hasAuthorizations(user, authorizations, envId);
    return { canAccess: () => authorized, isLoading: false };
  }

  if (namespaceAuthorizationsLoading) {
    return { canAccess: () => false, isLoading: true };
  }

  const authorizationsArray =
    typeof authorizations === 'string' ? [authorizations] : authorizations;
  const map = namespaceAuthorizations?.[envId];

  return {
    canAccess: (namespace) =>
      authorizationsArray.some(
        (authorization) => !!map?.[namespace]?.[authorization]
      ),
    isLoading: false,
  };
}

/**
 * Check if the user has some of the authorizations within a specific namespace.
 * See {@link useNamespaceAuthorizationChecker} for the underlying resolution logic.
 *
 * @param namespace the namespace to check authorizations against
 * @param authorizations a list of authorizations to check
 */
export function useNamespaceAuthorizations(
  namespace: string,
  authorizations: Authorization | Authorization[]
): { authorized: boolean; isLoading: boolean } {
  const { canAccess, isLoading } =
    useNamespaceAuthorizationChecker(authorizations);
  return { authorized: canAccess(namespace), isLoading };
}

/**
 * will return true if the user has some of the authorizations. assumes the user is authenticated and not an admin
 *
 * @private Please use `useAuthorizations` instead. Exported only for angular's authentication service app/portainer/services/authentication.js:154
 */
export function hasAuthorizations(
  user: User,
  authorizations: string | string[],
  environmentId?: EnvironmentId
) {
  if (!isBE) {
    return true;
  }

  const authorizationsArray =
    typeof authorizations === 'string' ? [authorizations] : authorizations;

  if (authorizationsArray.length === 0) {
    return true;
  }

  if (!environmentId) {
    return false;
  }

  const userEndpointAuthorizations =
    user.EndpointAuthorizations?.[environmentId] || {};

  return authorizationsArray.some(
    (authorization) => userEndpointAuthorizations[authorization]
  );
}

interface AuthorizedProps {
  authorizations: Authorization | Authorization[];
  environmentId?: EnvironmentId;
  adminOnlyCE?: boolean;
  childrenUnauthorized?: ReactNode;
}

export function Authorized({
  authorizations,
  environmentId,
  adminOnlyCE = false,
  children,
  childrenUnauthorized = null,
}: PropsWithChildren<AuthorizedProps>) {
  const { authorized } = useAuthorizations(
    authorizations,
    environmentId,
    adminOnlyCE
  );

  return authorized ? <>{children}</> : <>{childrenUnauthorized}</>;
}

interface AuthorizedToNamespaceProps {
  namespace: string;
  authorizations: Authorization | Authorization[];
  childrenUnauthorized?: ReactNode;
}

export function AuthorizedToNamespace({
  namespace,
  authorizations,
  children,
  childrenUnauthorized = null,
}: PropsWithChildren<AuthorizedToNamespaceProps>) {
  const { authorized } = useNamespaceAuthorizations(namespace, authorizations);

  return authorized ? <>{children}</> : <>{childrenUnauthorized}</>;
}

interface UserProviderProps {
  children: ReactNode;
}

// ultimately this provider should use the store directly and not use a separate memoized state
// see the implementation of useTableSettings()
export function UserProvider({ children }: UserProviderProps) {
  const userQuery = useLoadCurrentUser();
  const { environmentId } = useStore(environmentStore);
  const envQuery = useEnvironment(environmentId);
  const isAdmin = isEdgeAdmin(userQuery.data, envQuery.data);

  const authsQuery = useUserEnvironmentAuthorizations({
    environmentId,
    enabled: !userQuery.isLoading && !envQuery.isLoading && !isAdmin,
  });

  const isK8s = envQuery.data
    ? isKubernetesEnvironment(envQuery.data.Type)
    : false;
  // Skip the per-namespace query when the user already has cluster-wide
  // namespace access — those users get short-circuited to "all namespaces"
  // from the cluster auth map alone.
  const hasAllNamespaceAccess =
    !!environmentId &&
    !!authsQuery.data?.[environmentId]?.[ALL_NAMESPACES_ACCESS];
  const nsAuthsQuery = useUserKubernetesNamespaceAuthorizations({
    environmentId,
    enabled:
      !userQuery.isLoading &&
      !envQuery.isLoading &&
      !isAdmin &&
      isK8s &&
      authsQuery.data !== undefined &&
      !hasAllNamespaceAccess,
  });

  const providerState = useMemo((): State => {
    const namespaceAuthorizations =
      environmentId && nsAuthsQuery.data
        ? { [environmentId]: nsAuthsQuery.data }
        : undefined;

    return {
      user: userQuery.data,
      authorizations: authsQuery.data,
      namespaceAuthorizations,
      namespaceAuthorizationsLoading: nsAuthsQuery.isInitialLoading,
    };
  }, [
    userQuery.data,
    authsQuery.data,
    nsAuthsQuery.data,
    nsAuthsQuery.isInitialLoading,
    environmentId,
  ]);

  // sync to store so AJS can update
  useEffect(() => {
    currentUserStore.setState(providerState);
  }, [providerState]);

  if (!providerState.user) {
    return null;
  }

  return (
    <UserContext.Provider value={providerState}>
      {children}
    </UserContext.Provider>
  );
}
